import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { handleGlobalError, isBenignKiotaResponseParseError } from '@/composables/globalErrorHandler';
import { ApiClientFactory } from '@/stores/apiClientFactory';
import { CreateCategoryRequest, CreateManyProductsRequest, CreateProductRequest, PagedResultOfProduct, Product, AddPriceTierRequest, DeletePriceTierRequest, UpdatePriceTierRequest, AddProductAttributeRequest, DeleteProductAttributeRequest, UpdateProductRequest, EditProductAttributeRequest, UpdateProductPricesRequest_ProductPriceUpdate, UpdateProductPricesRequest, UpdateProductCategoriesRequest, UpdateProductTagsRequest, ReorderProductAttributesRequest, ReorderCategoriesRequest, ReorderProductsRequest, UpdateProductSortOrdersRequest, UpdateProductSortOrdersResponse, UpdateProductImagesRequest } from '@/api-client/models';
import { PagingParameters } from '@/types/DataTablePagingParameters';
import { RequestConfiguration, ResponseHandlerOption } from '@microsoft/kiota-abstractions';
import { ProductsRequestBuilderGetQueryParameters } from '@/api-client/api/products';
import { useAuthStore } from '@/stores/authStore';

export const useProductStore = defineStore('productStore', () => {
    const clientFactory = ApiClientFactory.getInstance();
    const client = clientFactory.getClient();
    const authStore = useAuthStore();

    const productsResponse = ref<PagedResultOfProduct>();
    const product = ref<Product | undefined>();
    const loading = ref(false);
    const productSearchLoading = ref(false);
    const activateLoading = ref(false);
    const totalRecords = ref(0);
    const showPricingTiersModal = ref(false);
    const showProductAttributesModal = ref(false);
    const deleteTierLoading = ref('');
    
    // Store table state for navigation preservation
    const lastPagingParameters = ref<PagingParameters | null>(null);
    const lastTableFilters = ref<any>(null);
    const tableStateKey = 'products-table-state';
    /** Matches PagingParameters' own default, used when rebuilding a partial persisted state. */
    const DEFAULT_ROWS_PER_PAGE = 25;

    const getProducts = async (pagingParameters: PagingParameters) => {
        try {
            loading.value = true;
            const params: RequestConfiguration<ProductsRequestBuilderGetQueryParameters> = {
                queryParameters: {
                    pageNumber: pagingParameters.PageNumber,
                    pageSize: pagingParameters.rows,
                    sortOn: pagingParameters.SortFieldProperCase,
                    sortDirection: pagingParameters.SortOrderAsString,
                    filters: pagingParameters.CleanedFilters ? [JSON.stringify(pagingParameters.CleanedFilters)] : [],
                    hasImages: pagingParameters.HasImages,
                    tag: pagingParameters.Tag
                }
            };

            productsResponse.value = await client.api.products.get(params);
            
            // Store the paging parameters for navigation preservation
            lastPagingParameters.value = new PagingParameters(pagingParameters.sortField, pagingParameters.sortOrder);
            lastPagingParameters.value.first = pagingParameters.first;
            lastPagingParameters.value.rows = pagingParameters.rows;
            lastPagingParameters.value.filters = pagingParameters.filters;
        } catch (error: any) {
            handleGlobalError(error);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Every product with no images, across all pages — the source for the missing-image CSV.
     * Deliberately ignores the table's filters and its own paging: this is a fixed report of the
     * whole catalog, not an export of the current view. Keeps off the shared `loading` and
     * `productsResponse` state so running it doesn't tear down the table the admin is looking at.
     * Returns undefined when the fetch failed (the error is already toasted).
     */
    const getProductsWithoutImages = async (): Promise<Product[] | undefined> => {
        const pageSize = 200;
        const products: Product[] = [];
        let pageNumber = 1;
        let totalPages = 1;

        try {
            do {
                const page = await client.api.products.get({
                    queryParameters: {
                        pageNumber,
                        pageSize,
                        sortOn: 'Sku',
                        sortDirection: 'asc',
                        // An empty filter set still has to travel: ListProductsRequest.Filters is a
                        // non-nullable List<QueryFilter>, and omitting the param fails model binding
                        // with "Value [] is not valid for a [List`1] property".
                        filters: [JSON.stringify([])],
                        hasImages: false
                    }
                });

                products.push(...(page?.data ?? []));
                // Trust the response's own page count: the API is free to cap pageSize below what
                // we asked for, which would make a locally computed total wrong.
                totalPages = page?.totalPages ?? 0;
                pageNumber++;
            } while (pageNumber <= totalPages);

            return products;
        } catch (error: any) {
            handleGlobalError(error);
        }
    };

    const getProduct = async (id: string) => {
        try {
            loading.value = true;
            product.value = await client.api.products.bySku(id).get();
        } catch (error: any) {
            handleGlobalError(error);
        } finally {
            loading.value = false;
        }
    };

    const searchProducts = async (searchTerm: string): Promise<Product[]> => {
        try {
            productSearchLoading.value = true;
            const skuFilter = JSON.stringify([
                { field: 'sku', operator: 'Contains', value: searchTerm }
            ]);
            const result = await client.api.products.get({
                queryParameters: {
                    pageNumber: 1,
                    pageSize: 25,
                    sortOn: 'Sku',
                    sortDirection: 'asc',
                    filters: [skuFilter]
                }
            });
            return result?.data ?? [];
        } catch (error: any) {
            handleGlobalError(error);
            return [];
        } finally {
            productSearchLoading.value = false;
        }
    };

    /**
     * The catalog products for a set of SKUs in one call, for a read beside the page's own data (the
     * simulator's scope check, CU-86bcj36z8). Quiet: a failure is an empty list, never a toast.
     */
    const getProductsBySkus = async (skus: string[]): Promise<Product[]> => {
        const wanted = Array.from(new Set(skus.map((sku) => sku?.trim()).filter(Boolean)));
        if (!wanted.length) return [];
        try {
            // The second argument is the request configuration the generated client reads from its last argument.
            const page = await client.api.products.batch.post({ skus: wanted }, {});
            return (page?.data ?? []) as Product[];
        } catch {
            return [];
        }
    };

    const getProductBySkuQuiet = async (sku: string): Promise<Product | undefined> => {
        try {
            return await client.api.products.bySku(sku).get();
        } catch {
            return undefined;
        }
    };

    /** True when the API took the update; a failure is reported through handleGlobalError and answers false. */
    const updateProduct = async (product: UpdateProductRequest): Promise<boolean> => {
        try {
            loading.value = true;
            product.images =[];
            const response = await client.api.products.update.post(product);
            return true;
        } catch (error: any) {
            handleGlobalError(error);
            return false;
        }
        finally {
            loading.value = false;
        }
    };

    /**
     * Replaces the product's image set and order in one call — position 0 becomes the default
     * thumbnail, an omitted image is removed from the product, an empty list clears them all.
     * The endpoint returns the updated product, which replaces the store's copy directly.
     * Throws on real failures so callers can abort follow-up work (e.g. deleting the asset).
     */
    const updateProductImages = async (sku: string, imageIds: string[]): Promise<Product | undefined> => {
        try {
            const request: UpdateProductImagesRequest = { sku, imageIds };
            const response = await client.api.products.images.put(request);
            if (response) {
                product.value = response;
            }
            return response;
        } catch (error: any) {
            if (isBenignKiotaResponseParseError(error)) {
                // The save landed; the body just would not parse. Refresh to pick up the new order.
                await getProduct(sku);
                return product.value;
            }
            handleGlobalError(error);
            throw error;
        }
    };

    const getCategories = async () => {
        try {
            loading.value = true;
            const categories = await client.api.categories.get();
            return categories;
        } catch (error: any) {
            handleGlobalError(error);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Create a category on the org, independent of any product. Returns the created
     * category (carrying the API's normalized path) or undefined when the call failed.
     * Deliberately does not touch the shared `loading` flag: this runs from inside an
     * open dropdown panel, and flipping the page-level spinner would tear it down.
     */
    const createCategory = async (name: string) => {
        try {
            const request: CreateCategoryRequest = { name };
            const created = await client.api.categories.post(request);

            // A new category holds nothing until a product is saved into it.
            refreshEmptyCategories();

            return created;
        } catch (error: any) {
            handleGlobalError(error);
        }
    };

    /**
     * The category paths no product sits in, directly or anywhere below them — the exact set
     * `deleteEmptyCategories` would remove, so its length is also the empty-category count. Held
     * on the store rather than in the Categories page because the sidebar badges the same number
     * from every route; one refresh updates both. It is read on the Categories page and after the
     * edits that can move it (a new category, a product's categories, an import), never on app
     * load: the API works it out from every category and product the organization has, which is
     * too slow to run on every page load.
     */
    const emptyCategories = ref<string[]>([]);
    const emptyCategoryCount = computed(() => emptyCategories.value.length);

    /**
     * Re-read the empty categories. Both endpoints are admin-only, so a non-admin is skipped
     * outright — calling would 403 and put an error toast on screen.
     * Deliberately leaves the shared `loading` flag alone: this runs beside the category tree and
     * after edits on other pages, and neither should drop the page back into its skeleton for a count.
     */
    const refreshEmptyCategories = async () => {
        if (!authStore.admin) return [];

        try {
            const response = await client.api.categories.empty.get();
            emptyCategories.value = response?.categories ?? [];
        } catch (error: any) {
            handleGlobalError(error);
        }

        return emptyCategories.value;
    };

    /**
     * Remove every empty category, and with it each removed category's image. The API resolves the
     * set at deletion time rather than taking one from us, so a category a product moved into since
     * the preview survives — which means the returned paths are the truth about what went, and can
     * differ from what the confirmation dialog listed.
     */
    const deleteEmptyCategories = async () => {
        try {
            loading.value = true;
            const response = await client.api.categories.empty.delete();
            const removed = response?.removed ?? [];

            // Whatever survived the prune is what the sidebar should now be badging.
            await refreshEmptyCategories();

            return removed;
        } catch (error: any) {
            handleGlobalError(error);
            return undefined;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Rearrange one level of the category tree. Position drives the order categories are displayed
     * in across the storefront, the sales app and here, so admins arrange it on the Categories
     * screen. The API takes the level's whole sequence and rejects anything that isn't a pure
     * rearrangement, so always send every category on that level.
     */
    const reorderCategories = async (request: ReorderCategoriesRequest) => {
        try {
            await client.api.categories.reorder.put(request);
        } catch (error: any) {
            // Kiota reports an empty 2xx body as a parse error; only surface real failures.
            if (!isBenignKiotaResponseParseError(error)) {
                handleGlobalError(error);
            }
        }
    };

    /**
     * Persist the order of a category's products. The endpoint only accepts a complete
     * rearrangement — every SKU in the category, once each — so callers arrange the full list
     * before saving. Rethrows so the caller can restore the order the server still holds; a
     * rejected reorder that looked like it saved is worse than one that visibly snaps back.
     */
    const reorderProducts = async (request: ReorderProductsRequest) => {
        try {
            await client.api.products.reorder.put(request);
        } catch (error: any) {
            // Kiota reports an empty 2xx body as a parse error; only surface real failures.
            if (!isBenignKiotaResponseParseError(error)) {
                handleGlobalError(error);
                throw error;
            }
        }
    };

    /**
     * Set positions by SKU from an import. Unknown SKUs come back in the response rather than
     * failing the batch, so the caller can tell the admin what did not land.
     */
    const updateProductSortOrders = async (request: UpdateProductSortOrdersRequest): Promise<UpdateProductSortOrdersResponse | undefined> => {
        try {
            return await client.api.products.sortorders.put(request);
        } catch (error: any) {
            if (isBenignKiotaResponseParseError(error)) return undefined;
            handleGlobalError(error);
            throw error;
        }
    };

    /**
     * The org's distinct product tags with a product count each, ordered by tag. Served by the
     * API's grouped query — never derived by scanning the catalog client-side.
     */
    const getProductTags = async () => {
        try {
            loading.value = true;
            return await client.api.products.tags.get();
        } catch (error: any) {
            handleGlobalError(error);
        } finally {
            loading.value = false;
        }
    };

    const getSubCategories = async (categoryId: string) => {
        try {
            loading.value = true;
            const subCategories = await client.api.categories.byCategory(categoryId).get();
            return subCategories;
        } catch (error: any) {
            handleGlobalError(error);
        } finally {
            loading.value = false;
        }
    };

    /**
     * The product behind a SKU, or null when the organization has none.
     *
     * The API answers an unknown SKU with an empty 404 — no body, no content type — which the
     * generated client cannot turn into a typed error (it fails parsing the nothing, with no status
     * on what it throws). So the status is read off the raw response first; only a hit, or a
     * failure, is then read through the client as usual, which keeps the hit typed and the failure
     * reported and rethrown.
     */
    const findProductBySku = async (sku: string): Promise<Product | null> => {
        // The handler's result stands in for the response body. It has to be an object, not the
        // status itself and never undefined: the factory's send wrapper treats a falsy result as
        // no response at all.
        const statusOnly = new ResponseHandlerOption();
        statusOnly.responseHandler = {
            handleResponse: async <NativeResponseType, ModelType>(response: NativeResponseType) =>
                ({ status: (response as Response).status }) as ModelType
        };

        try {
            const seen = (await client.api.products.bySku(sku).get({ options: [statusOnly] })) as unknown as { status: number };
            if (seen.status === 404) return null;
            return (await client.api.products.bySku(sku).get()) ?? null;
        } catch (error: any) {
            handleGlobalError(error);
            throw error;
        }
    };

    /**
     * Add one product. `PUT api/products` inserts — a SKU the organization already has fails with
     * a generic message — so callers ask findProductBySku first. Returns the product as the API
     * stored it; the failure is reported and rethrown so a dialog can stay open.
     */
    const createProduct = async (request: CreateProductRequest): Promise<Product> => {
        try {
            const created = await client.api.products.put(request);
            if (!created) {
                throw new Error('The product was created, but the API returned nothing to open it by.');
            }
            return created;
        } catch (error: any) {
            handleGlobalError(error);
            throw error;
        }
    };

    const saveProduct = async (product: Product) => {

    };

    const saveManyProducts = async (data: any[]) => {
        
        try {
            loading.value = true;
            const products: CreateProductRequest[] = [];
            data.forEach((item: CreateProductRequest) => {
                products.push(item);
            });

            const request: CreateManyProductsRequest = {
                products: products
            };
          
            await client.api.products.bulk.put(request);

        } catch (error: any) {

            if (isBenignKiotaResponseParseError(error)) return;
            handleGlobalError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const saveProductPrices = async (request: UpdateProductPricesRequest) => {
        try {
            loading.value = true;
            await client.api.products.prices.put(request);
        } catch (error: any) {
            if (isBenignKiotaResponseParseError(error)) return;
            handleGlobalError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const saveProductCategories = async (request: UpdateProductCategoriesRequest) => {
        try {
            loading.value = true;
            await client.api.products.categories.put(request);
        } catch (error: any) {
            if (isBenignKiotaResponseParseError(error)) return;
            handleGlobalError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Bulk-apply tags by SKU (replace-per-SKU: an empty tags array clears a SKU's tags).
     * Returns the applied/skipped summary from the API (unknown SKUs are skipped, not failed).
     */
    const saveProductTags = async (request: UpdateProductTagsRequest) => {
        try {
            loading.value = true;
            return await client.api.products.tags.put(request);
        } catch (error: any) {
            if (isBenignKiotaResponseParseError(error)) return undefined;
            handleGlobalError(error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const setShowPricingTiersModal = (isShown: boolean) => {
        showPricingTiersModal.value = isShown;
    }

    const addPriceTier = async (addPriceTierRequest: AddPriceTierRequest) => {
        try {
            await client.api.organizations.priceTiers.post(addPriceTierRequest);
        }
        catch (error: any) {
            // Kiota reports an empty 2xx body as a parse error; only surface real failures.
            if (!isBenignKiotaResponseParseError(error)) {
                handleGlobalError(error);
            }
        }
    }

    const deletePriceTier = async (deletePriceTierRequest: DeletePriceTierRequest) => {
        deleteTierLoading.value = deletePriceTierRequest.name as string;
        try {
            const response = await client.api.organizations.priceTiers.delete(deletePriceTierRequest);
        }
        catch (error: any) {
            // Kiota reports an empty 2xx body as a parse error; only surface real failures.
            if (!isBenignKiotaResponseParseError(error)) {
                handleGlobalError(error);
            }
        }
        finally {
            deleteTierLoading.value = 'false';
        }
    }

    const addProductAttribute= async (addProductAttributeRequest: AddProductAttributeRequest) => {
        try {
            await client.api.organizations.productAttributes.post(addProductAttributeRequest);
        }
        catch (error: any) {
            // Kiota reports an empty 2xx body as a parse error; only surface real failures.
            if (!isBenignKiotaResponseParseError(error)) {
                handleGlobalError(error);
            }
        }
    }

    const deleteProductAttribute = async (deleteProductAttributeRequest: DeleteProductAttributeRequest) => {
            try {
                await client.api.organizations.productAttributes.delete(deleteProductAttributeRequest);
            }
            catch (error: any) {
                // Kiota reports an empty 2xx body as a parse error; only surface real failures.
                if (!isBenignKiotaResponseParseError(error)) {
                    handleGlobalError(error);
                }
            }
        }

    // Position in the registry is the display order on the grids and detail pages. The API takes
    // the whole slug sequence and rejects anything that isn't a pure rearrangement, so callers
    // send the full list on each drop. Failures surface globally; the caller re-syncs from the org.
    const reorderProductAttributes = async (reorderProductAttributesRequest: ReorderProductAttributesRequest) => {
        try {
            await client.api.organizations.productAttributes.reorder.put(reorderProductAttributesRequest);
        }
        catch (error: any) {
            if (!isBenignKiotaResponseParseError(error)) {
                handleGlobalError(error);
            }
        }
    }

    const editProductAttribute = async (editProductAttribute: EditProductAttributeRequest) => {

        try {
            await client.api.organizations.productAttributes.put(editProductAttribute);
        }
        catch (error: any) {
            // Kiota reports an empty 2xx body as a parse error; only surface real failures.
            if (!isBenignKiotaResponseParseError(error)) {
                handleGlobalError(error);
            }
        }
    }

    const activate = async (sku: string) => {
        try {
            activateLoading.value = true;
            const response = await client.api.products.activate.bySku(sku).patch();
            console.log(response);
        } catch (error: any) {
            handleGlobalError(error);

        } finally {
            activateLoading.value = false;
        }
    }
    const deactivate = async (sku: string) => {
        try {
            activateLoading.value = true;
            const response = await client.api.products.deactivate.bySku(sku).patch();
            console.log(response);
        } catch (error: any) {
            handleGlobalError(error);
        } finally {
            activateLoading.value = false;
        }
    }



    const setShowProductAttributesModal = (isShown: boolean) => {
        showProductAttributesModal.value = isShown;
    }

    const setTableFilters = (filters: any) => {
        lastTableFilters.value = filters;
    };

    const getStoredTableState = () => {
        return {
            pagingParameters: lastPagingParameters.value,
            tableFilters: lastTableFilters.value
        };
    };

    const clearStoredTableState = (clearProducts = false) => {
        lastPagingParameters.value = null;
        lastTableFilters.value = null;
        // Drop the DataTable's persisted *navigation* state — page, sort, filters — but keep the
        // column widths. Those are a layout choice the user dragged deliberately, not navigation
        // state, and this runs on every trip out of /products (router.beforeEach), so removing the
        // whole entry threw them away as soon as anyone visited another screen. Harmless before
        // CU-86bbvdchd, when `stateKey` was undefined and this deleted a key that never existed.
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const raw = localStorage.getItem(tableStateKey);
                const parsed = raw ? JSON.parse(raw) : null;
                const preserved: Record<string, any> = {};

                if (parsed?.columnWidths !== undefined) preserved.columnWidths = parsed.columnWidths;
                if (parsed?.tableWidth !== undefined) preserved.tableWidth = parsed.tableWidth;

                if (Object.keys(preserved).length > 0) {
                    // PrimeVue's restoreState assigns `first`/`rows` unconditionally whenever the
                    // table has a paginator, so an entry missing them leaves the paginator with an
                    // undefined page size. Reset the position explicitly and keep the page size.
                    preserved.first = 0;
                    preserved.rows = typeof parsed?.rows === 'number' ? parsed.rows : DEFAULT_ROWS_PER_PAGE;
                    localStorage.setItem(tableStateKey, JSON.stringify(preserved));
                } else {
                    localStorage.removeItem(tableStateKey);
                }
            } catch {
                // Unreadable entry: fall back to removing it outright rather than leaving it.
                localStorage.removeItem(tableStateKey);
            }
        }
        // Optionally clear products data (e.g., when coming from category preview)
        if (clearProducts) {
            productsResponse.value = undefined;
        }
    };

    return {
        productsResponse,
        product,
        loading,
        productSearchLoading,
        activateLoading,
        totalRecords,
        showPricingTiersModal,
        showProductAttributesModal,
        getCategories,
        emptyCategories,
        emptyCategoryCount,
        refreshEmptyCategories,
        deleteEmptyCategories,
        reorderCategories,
        reorderProducts,
        updateProductSortOrders,
        createCategory,
        getSubCategories,
        getProductTags,
        getProducts,
        getProductsWithoutImages,
        getProduct,
        searchProducts,
        getProductBySkuQuiet,
        getProductsBySkus,
        createProduct,
        findProductBySku,
        saveProduct, 
        saveManyProducts,
        saveProductPrices,
        saveProductCategories,
        saveProductTags,
        setShowPricingTiersModal,
        setShowProductAttributesModal, 
        addPriceTier, 
        deletePriceTier, 
        deleteTierLoading, 
        addProductAttribute, 
        deleteProductAttribute, 
        editProductAttribute,
        reorderProductAttributes,
        updateProduct,
        updateProductImages,
        activate, 
        deactivate,
        setTableFilters,
        getStoredTableState,
        clearStoredTableState,
        tableStateKey 
    };    
});
